import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, MailCheck, KeyRound } from 'lucide-react';
import { api, setToken } from '../lib/api';
import { toast } from '../hooks/use-toast';

// Both halves of the same job live here: asking for the link, and using it.
// The token in the query string decides which one is on screen, so the link in
// the email lands somewhere that already knows what to do with it.
export default function Reset() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const ask = async () => {
    if (!email.trim()) { toast({ title: 'Enter your email first' }); return; }
    setBusy(true);
    try {
      const r = await api.forgotPassword({ email: email.trim() });
      setSent(true);
      toast({ title: 'Check your inbox', description: r.message });
    } catch (e) {
      toast({ title: 'Could not send the link', description: e.message });
    } finally { setBusy(false); }
  };

  const apply = async () => {
    if ((password || '').length < 8) {
      toast({ title: 'Use at least 8 characters' });
      return;
    }
    setBusy(true);
    try {
      const r = await api.resetPassword({ token, password });
      // Signed in on the spot: asking someone to retype the password they just
      // chose is one more place to lose them.
      setToken(r.token);
      toast({ title: 'Password changed', description: 'You are signed in.' });
      navigate('/app');
    } catch (e) {
      toast({ title: 'Could not reset it', description: e.message });
    } finally { setBusy(false); }
  };

  const field = 'w-full rounded-md border border-[#2A2A30] bg-[#0E0E10] px-3 py-2.5 text-sm text-white placeholder-white/30 focus:border-node-model focus:outline-none';

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#0E0E10] px-6 text-white">
      <div className="w-full max-w-sm rounded-2xl border border-[#2A2A30] bg-[#141417] p-7">
        <div className="flex items-center gap-2">
          <KeyRound size={18} className="text-node-model" />
          <h1 className="font-serif-display text-2xl">
            {token ? 'Choose a new password' : 'Reset your password'}
          </h1>
        </div>

        {token ? (
          <>
            <p className="mt-2 text-sm text-white/50">
              Pick something you have not used elsewhere. At least 8 characters.
            </p>
            <input type="password" value={password} autoFocus data-testid="reset-password"
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') apply(); }}
              placeholder="New password" className={`mt-5 ${field}`} />
            <button onClick={apply} disabled={busy} data-testid="reset-submit"
              className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-white py-2.5 text-sm font-medium text-black hover:bg-white/90 disabled:opacity-50">
              {busy ? <Loader2 size={15} className="animate-spin" /> : null}
              Set my password
            </button>
          </>
        ) : sent ? (
          <div className="mt-5 flex items-start gap-2.5 rounded-lg border border-[#2A2A30] bg-[#0E0E10] p-4">
            <MailCheck size={16} className="mt-0.5 shrink-0 text-emerald-400" />
            <p className="text-sm leading-relaxed text-white/70">
              If that address has an account, a link is on its way. It works for one hour.
              Check the spam folder if it is not there in a minute.
            </p>
          </div>
        ) : (
          <>
            <p className="mt-2 text-sm text-white/50">
              Enter the address you signed up with and we will send you a link.
            </p>
            <input type="email" value={email} autoFocus data-testid="forgot-email"
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') ask(); }}
              placeholder="you@example.com" className={`mt-5 ${field}`} />
            <button onClick={ask} disabled={busy} data-testid="forgot-submit"
              className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-white py-2.5 text-sm font-medium text-black hover:bg-white/90 disabled:opacity-50">
              {busy ? <Loader2 size={15} className="animate-spin" /> : null}
              Send me a link
            </button>
          </>
        )}

        <p className="mt-5 text-center text-xs text-white/40">
          <Link to="/login" className="hover:text-white">Back to sign in</Link>
        </p>
      </div>
    </div>
  );
}
